
import React, { useState, useEffect } from "react";
import CalculatorFramework from "../shared/CalculatorFramework";
import PressureAdvanceFastMode from "../modes/PressureAdvanceFastMode";
import SlicerSelection from "../tutorial/SlicerSelection";
import PressureAdvanceInstructions from "../tutorial/PressureAdvanceInstructions";
import PressureAdvanceMeasurementInput from "../tutorial/PressureAdvanceMeasurementInput";
import PressureAdvanceResultsView from "../tutorial/PressureAdvanceResultsView";

const PressureAdvanceSteps = () => {
  const [selectedSlicer, setSelectedSlicer] = useState<"creality" | "orca" | "anycubic">("orca");
  const [pressureAdvanceStart, setPressureAdvanceStart] = useState("");
  const [pressureAdvanceStep, setPressureAdvanceStep] = useState("");
  const [measuredHeight, setMeasuredHeight] = useState("");
  const [tunedPressureAdvance, setTunedPressureAdvance] = useState("");

  // Normalize decimal separators to standard decimal point
  const normalizeDecimalSeparator = (value: string) => {
    return value.replace(/[,''٫]/g, '.');
  };

  // Format number to remove trailing zeroes
  const formatNumber = (value: number) => {
    return parseFloat(value.toFixed(6)).toString();
  };

  // Reset all variables function
  const resetAllVariables = () => {
    setSelectedSlicer("orca");
    setPressureAdvanceStart("");
    setPressureAdvanceStep("");
    setMeasuredHeight("");
    setTunedPressureAdvance("");
  };

  // Calculate tuned pressure advance with proper decimal separator handling and clean formatting
  useEffect(() => {
    if (pressureAdvanceStart && pressureAdvanceStep && measuredHeight) {
      const normalizedStart = normalizeDecimalSeparator(pressureAdvanceStart);
      const normalizedStep = normalizeDecimalSeparator(pressureAdvanceStep);
      const normalizedHeight = normalizeDecimalSeparator(measuredHeight);
      
      const start = parseFloat(normalizedStart);
      const step = parseFloat(normalizedStep);
      const height = parseFloat(normalizedHeight);
      
      if (!isNaN(start) && !isNaN(step) && !isNaN(height)) {
        const result = start + (step * height);
        setTunedPressureAdvance(formatNumber(result));
      }
    } else {
      setTunedPressureAdvance("");
    }
  }, [pressureAdvanceStart, pressureAdvanceStep, measuredHeight]);

  const startOver = () => {
    resetAllVariables();
  };

  const goToTutorial = () => {
    resetAllVariables();
  };

  return (
    <CalculatorFramework>
      {({ currentStep, setCurrentStep, mode, setMode, handleBackToModeSelection }) => {
        // Fast Mode Component
        if (mode === "fast") {
          return (
            <PressureAdvanceFastMode
              pressureAdvanceStart={pressureAdvanceStart}
              onPressureAdvanceStartChange={setPressureAdvanceStart}
              pressureAdvanceStep={pressureAdvanceStep}
              onPressureAdvanceStepChange={setPressureAdvanceStep}
              measuredHeight={measuredHeight}
              onMeasuredHeightChange={setMeasuredHeight}
              tunedPressureAdvance={tunedPressureAdvance}
              onGoToTutorial={() => {
                resetAllVariables();
                setMode("tutorial");
                setCurrentStep(1);
              }}
            />
          );
        }

        // Tutorial Mode Steps
        if (mode === "tutorial") {
          // Tutorial Step 1: Slicer Selection
          if (currentStep === 1) {
            return (
              <SlicerSelection
                selectedSlicer={selectedSlicer}
                onSlicerChange={setSelectedSlicer}
                onNext={() => setCurrentStep(2)}
                onPrevious={handleBackToModeSelection}
              />
            );
          }

          // Tutorial Step 2: Pressure Advance Instructions
          if (currentStep === 2) {
            return (
              <PressureAdvanceInstructions
                selectedSlicer={selectedSlicer}
                onNext={() => setCurrentStep(3)}
                onPrevious={() => setCurrentStep(1)}
              />
            );
          }

          // Tutorial Step 3: Measurement Input
          if (currentStep === 3) {
            return (
              <PressureAdvanceMeasurementInput
                pressureAdvanceStart={pressureAdvanceStart}
                onPressureAdvanceStartChange={setPressureAdvanceStart}
                pressureAdvanceStep={pressureAdvanceStep}
                onPressureAdvanceStepChange={setPressureAdvanceStep}
                measuredHeight={measuredHeight}
                onMeasuredHeightChange={setMeasuredHeight}
                onNext={() => setCurrentStep(4)}
                onPrevious={() => setCurrentStep(2)}
              />
            );
          }

          // Tutorial Step 4: Results
          if (currentStep === 4 && tunedPressureAdvance) {
            return (
              <PressureAdvanceResultsView
                pressureAdvanceStart={pressureAdvanceStart}
                pressureAdvanceStep={pressureAdvanceStep}
                measuredHeight={measuredHeight}
                tunedPressureAdvance={tunedPressureAdvance}
                onPrevious={() => setCurrentStep(3)}
                onStartOver={() => {
                  startOver();
                  handleBackToModeSelection();
                }}
              />
            );
          }
        }

        return null;
      }}
    </CalculatorFramework>
  );
};

export default PressureAdvanceSteps;
